import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import SignaturePad from "signature_pad";
import { useDocument } from "../hooks/useDocument";
import SignatureSuccess from "./SignatureSucess";
import SignatureConfirmationPopup from "./SignatureConfirmationPopup";
import { toast } from "react-toastify";
import ErrorPage from "../pages/ErrorPage";
import CapabilitiesStyles from "./CapabilitiesStyles";
import { useMediaQuery } from "react-responsive";
import InvoiceContent from "./InvoiceContent";
import PaymentForm from "./PaymentForm";
import { apiAxios } from "../functions/axiosInstance";

const PublicDocumentView = () => {
  const isMobile = useMediaQuery({
    query: "(max-width: 500px)",
  });
  const { token } = useParams();
  // Advisory recipient token from the emailed link. The browser has it in the URL, but
  // the notification fires on the API call, so it has to be forwarded there or the open
  // reports as "Anonymous Viewer".
  const [searchParams] = useSearchParams();
  const recipientToken = searchParams.get("r");
  const navigate = useNavigate();
  const [showSignatureConfirm, setShowSignatureConfirm] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [pendingSignature, setPendingSignature] = useState(null);
  const [documentId, setDocumentId] = useState(null); // Add this state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const documentRef = useRef(null);
  const [selectedSupport, setSelectedSupport] = useState(null); // State for support package
  const [selectedPayment, setSelectedPayment] = useState(null); // State for payment option
  const [signerName, setSignerName] = useState(""); // State for signer name

  const FloatingDownloadButton = ({ isDownloading, onDownload }) => (
    <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50">
      <button
        onClick={onDownload}
        disabled={isDownloading}
        className="px-3 py-2 sm:px-4 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 shadow-lg flex items-center gap-2 transition-colors duration-200 text-sm sm:text-base min-h-[44px]"
      >
        {isDownloading ? (
          <>
            <span className="animate-spin">⏳</span>
            <span>Downloading...</span>
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span>Download PDF</span>
          </>
        )}
      </button>
    </div>
  );

  // Function to check if document has signature box
  const hasSignatureBox = (content) => {
    return content?.includes("signature-container") || false;
  };

  // Get the signDocument mutation from our hook
  const {
    document,
    isLoading,
    isError,
    error,
    signDocument,
    isSigningDocument,
    signError,
  } = useDocument(undefined, token, undefined, recipientToken);

  // Anonymous share-link viewers have no auth token, so downloads go through
  // the public, token-validated share route instead of the staff/portal-owner
  // gated /documents/download/{id} route.
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (document?._id) {
      setDocumentId(document._id);
      // Also store in sessionStorage as backup
      sessionStorage.setItem("lastDocumentId", document._id);
    }
  }, [document]);

  // Add download handler function
  const handleDownload = async () => {
    if (!token) {
      toast.error("Unable to download document at this time");
      return;
    }
    setIsDownloading(true);
    try {
      const response = await apiAxios.get(`/documents/shared/${token}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", `document-${document?._id || "document"}.pdf`);
      window.document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Unable to download document at this time");
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle signature confirmation
  const handleSignConfirm = async () => {
    if (!pendingSignature || isSubmitting || isSigningDocument) return;

    setIsSubmitting(true);

    const { name, signaturePad, signerIndex } = pendingSignature;

    try {
      // Get signature image as base64
      const signatureImg = signaturePad.toDataURL();

      // Get selected payment option from DOM (Support package comes from state)
      let selectedPaymentOption = null;
      if (documentRef.current) {
        const paymentCard = documentRef.current.querySelector(
          ".payment-option.selected"
        );
        if (paymentCard) {
          selectedPaymentOption = paymentCard.getAttribute(
            "data-payment-option"
          );
        }
      }

      // Determine requirement flags (default to true)
      const requireSupport = document.require_support_selection ?? true;
      const requirePayment = document.require_payment_selection ?? true;
      // Enforce support package selection if required
      if (requireSupport && !selectedSupport) {
        toast.error("Please select a support package (or None) before signing.");
        setIsSubmitting(false);
        return;
      }
      // Enforce payment option selection if required
      if (requirePayment && !selectedPaymentOption) {
        toast.error("Please select a payment option before signing.");
        setIsSubmitting(false);
        return;
      }

      // Assign the confirmed selected support package from state
      const selectedSupportPackage = selectedSupport;
      // If no payment option selected, set to "default"
      if (!selectedPaymentOption) {
        selectedPaymentOption = "default";
      }

      // Create signature data object
      const signatureData = {
        signer_name: name,
        signature_img: signatureImg,
        signature_date: new Date().toISOString(),
        selected_payment_option: selectedPaymentOption,
        selected_support_package: selectedSupportPackage,
      };
      // Include the signer index for multi-signer documents (omitted for
      // legacy single-signer docs, where the backend targets the open slot).
      if (signerIndex !== null && signerIndex !== undefined) {
        signatureData.signer_index = signerIndex;
      }

      // No need to pass token - it's handled by the hook
      const result = await signDocument(signatureData);

      setShowSignatureConfirm(false);
      setPendingSignature(null);

      // Determine whether every required signer has now signed.
      const resultSigners = result?.signers;
      const fullySigned = Array.isArray(resultSigners) && resultSigners.length > 0
        ? resultSigners.every((s) => !!s.signed_at)
        : !!result?.signed_at;

      if (fullySigned) {
        // All parties have signed: show the success page.
        setShowSuccessPage(true);
      } else {
        // Partial signature: reload so the server re-renders with this signer
        // locked read-only and the next signer's pad ready to sign.
        toast.success("Signature recorded. Awaiting the remaining signer(s).");
        setTimeout(() => window.location.reload(), 1200);
      }
    } catch (error) {
      console.error("Signing error:", error);
      toast.error("Failed to sign document. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const initializeSignaturePad = () => {
    if (!documentRef.current) return;

    // One canvas is rendered per not-yet-signed signer. Suffixed ids
    // (signature-pad-0, signature-pad-1, ...) keep multiple signers isolated;
    // the legacy single-signer markup (#signature-pad) is still supported.
    const canvases = Array.from(
      documentRef.current.querySelectorAll(
        "canvas.signature-pad-canvas, canvas#signature-pad"
      )
    );
    if (!canvases.length) {
      console.error("No signature pad canvas found");
      return;
    }

    const drawName = (canvas, name) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.font = "italic 30px 'Brush Script MT', cursive";
      ctx.fillStyle = "black";
      const textWidth = ctx.measureText(name).width;
      const xPosition = (canvas.width - textWidth) / 2;
      const yPosition = canvas.height / 2;
      ctx.fillText(name, xPosition, yPosition);
    };

    // Wire up each signer's pad independently.
    canvases.forEach((canvas) => {
      const rawOrder = canvas.getAttribute("data-signer-order");
      // signerIndex stays null for legacy single-signer markup.
      const signerIndex =
        rawOrder !== null && rawOrder !== "" ? parseInt(rawOrder, 10) : null;
      const suffix = signerIndex !== null ? `-${signerIndex}` : "";

      const signaturePad = new SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 3,
        penColor: "black",
      });

      const clearButton = documentRef.current.querySelector(
        `#clear-button${suffix}`
      );
      const signButton = documentRef.current.querySelector(
        `#sign-button${suffix}`
      );
      const nameInput = documentRef.current.querySelector(
        `#name-input${suffix}`
      );
      const signDateElement = documentRef.current.querySelector(
        `#sign-date${suffix}`
      );

      // Already-signed slot rendered with a disabled name: paint and lock it.
      if (nameInput?.disabled) {
        const name = nameInput.value;
        if (name) {
          drawName(canvas, name);
          signaturePad.off();
          canvas.style.opacity = "0.9";
          if (clearButton) clearButton.disabled = true;
          if (signButton) signButton.disabled = true;
          return;
        }
      }

      const generateSignature = (name) => {
        signaturePad.clear();
        drawName(canvas, name);
      };

      if (clearButton) {
        clearButton.addEventListener("click", () => {
          signaturePad.clear();
          if (signDateElement) signDateElement.textContent = "";
          if (nameInput) {
            nameInput.value = "";
            setSignerName("");
          }
        });
      }

      if (nameInput) {
        // The name input starts empty; each signer types their own name, which
        // generates the cursive signature on their pad.
        nameInput.addEventListener("input", function () {
          const name = this.value.trim();
          if (name) {
            generateSignature(name);
          } else {
            signaturePad.clear();
          }
        });
      }

      if (signButton) {
        // Ensure the sign button is enabled.
        signButton.disabled = false;
        signButton.removeAttribute("disabled");
        signButton.addEventListener("click", () => {
          // Determine requirement flags (default to true)
          const requireSupport = document.require_support_selection ?? true;
          const requirePayment = document.require_payment_selection ?? true;
          // Check support selection from DOM
          if (requireSupport) {
            const supportCard = documentRef.current.querySelector(
              ".support-package.selected"
            );
            const supportNoneRadio = documentRef.current.querySelector(
              "#support-package-none-radio"
            );
            const supportNoneChecked = supportNoneRadio
              ? supportNoneRadio.checked
              : false;
            if (!supportCard && !supportNoneChecked) {
              toast.error(
                "Please select a support package (or None) before signing."
              );
              return;
            }
          }
          // Check payment selection from DOM
          let selectedPaymentOption = null;
          const paymentCard = documentRef.current.querySelector(
            ".payment-option.selected"
          );
          if (paymentCard) {
            selectedPaymentOption = paymentCard.getAttribute(
              "data-payment-option"
            );
          }
          if (requirePayment && !selectedPaymentOption) {
            toast.error("Please select a payment option before signing.");
            return;
          }
          // Validate signer name
          const name = nameInput ? nameInput.value.trim() : "";
          if (!name) {
            toast.error("Please enter your name before signing.");
            return;
          }
          // All checks passed, proceed to confirmation
          setSignerName(name);
          setPendingSignature({
            name,
            signaturePad,
            signerIndex,
            expectedName: nameInput?.getAttribute("data-expected") || null,
          });
          setShowSignatureConfirm(true);
        });
      }
    });

    const downloadButton =
      documentRef.current.querySelector("#download-button");
    if (downloadButton) {
      downloadButton.addEventListener("click", handleDownload);
    }
  };

  useEffect(() => {
    if (document?.content && documentRef.current) {
      if (document.content.includes("signature-pad")) {
        // The bundled import initializes directly (no CDN script/onload wait).
        initializeSignaturePad();
      } else {
        // For paid invoices, update the status badge text baked into the static HTML.
        if (document?.invoice_status === 'paid') {
          const statusEl = documentRef.current.querySelector('.invoice-status, [class*="status"]');
          if (statusEl && statusEl.textContent?.trim().toUpperCase() === 'PENDING') {
            statusEl.textContent = 'PAID';
            statusEl.style.color = '#16a34a';
          }
          // Also do a text replace on any element showing "PENDING"
          documentRef.current.querySelectorAll('*').forEach(el => {
            if (el.children.length === 0 && el.textContent?.trim() === 'PENDING') {
              el.textContent = 'PAID';
              el.style.color = '#16a34a';
              el.style.fontWeight = '600';
            }
          });
        }

        // invoice_template.py still bakes a static, non-interactive payment form
        // into the document HTML. PaymentForm.js (rendered as a real React
        // component below) is now the single payment UI, so drop that dead
        // markup instead of mounting Stripe Elements into it.
        ["payment-form-container", "payment-processing", "payment-success", "payment-error"].forEach(
          (id) => documentRef.current.querySelector(`#${id}`)?.remove()
        );

        const downloadButton =
          documentRef.current.querySelector("#download-button");
        if (downloadButton) {
          console.log("Download button found, adding listener");
          downloadButton.addEventListener("click", handleDownload);
        } else {
          console.log("No download button found, not adding listener");
        }
      }

      // --- Begin: Payment/Support Card Selection Logic ---
      // Only run if both payment and support cards exist
      const paymentCards = Array.from(
        documentRef.current.querySelectorAll(".payment-option")
      );
      const supportCards = Array.from(
        documentRef.current.querySelectorAll(".support-package")
      );
      const signButton = documentRef.current.querySelector("#sign-button");
      const supportNoneRadio = documentRef.current.querySelector(
        "#support-package-none-radio"
      );

      // Use state for payment option selection
      // Remove local selectedSupport and updateSignButton function

      function handlePaymentClick(e) {
        paymentCards.forEach((c) => c.classList.remove("selected"));
        e.currentTarget.classList.add("selected");
        const paymentOption = e.currentTarget.getAttribute(
          "data-payment-option"
        );
        setSelectedPayment(paymentOption);
        // No need to call updateSignButton here, state change will trigger effect
      }

      function handleSupportClick(e) {
        // Deselect radio button if a card is selected
        if (supportNoneRadio) supportNoneRadio.checked = false;
        supportCards.forEach((c) => c.classList.remove("selected"));
        e.currentTarget.classList.add("selected");
        // Update state instead of local variable
        setSelectedSupport(
          e.currentTarget.getAttribute("data-support-package")
        );
      }

      function handleSupportNoneRadioChange(e) {
        if (e.target.checked) {
          // Deselect all support cards
          supportCards.forEach((c) => c.classList.remove("selected"));
          // Update state instead of local variable
          setSelectedSupport("none");
        } else {
          // If unchecked, potentially clear state if no card is selected
          const anyCardSelected = supportCards.some((c) =>
            c.classList.contains("selected")
          );
          if (!anyCardSelected) {
            setSelectedSupport(null);
          }
        }
      }
      // Removed extra closing brace here

      paymentCards.forEach((card) => {
        card.addEventListener("click", handlePaymentClick);
      });
      supportCards.forEach((card) => {
        card.addEventListener("click", handleSupportClick);
      });
      if (supportNoneRadio) {
        supportNoneRadio.addEventListener(
          "change",
          handleSupportNoneRadioChange
        );
      }

      // --- Add disabled class and conditionally add listeners based on signed status ---
      const isSigned = !!document.signed_at;
      const paymentOptionsGrid = paymentCards[0]?.closest(".expertise-grid"); // Find payment grid via first card
      const supportOptionsGrid = supportCards[0]?.closest(".expertise-grid"); // Find support grid via first card
      const supportNoneContainer = supportNoneRadio?.closest("div"); // Find container for 'None' radio

      if (isSigned) {
        // Add disabled class if signed
        paymentOptionsGrid?.classList.add("options-disabled");
        supportOptionsGrid?.classList.add("options-disabled");
        supportNoneContainer?.classList.add("options-disabled");
        // Also disable individual cards and radio
        paymentCards.forEach((card) => {
          card.removeEventListener("click", handlePaymentClick);
          card.classList.add("options-disabled");
          card.style.pointerEvents = "none";
        });
        supportCards.forEach((card) => {
          card.removeEventListener("click", handleSupportClick);
          card.classList.add("options-disabled");
          card.style.pointerEvents = "none";
        });
        if (supportNoneRadio) {
          supportNoneRadio.removeEventListener(
            "change",
            handleSupportNoneRadioChange
          );
          supportNoneRadio.disabled = true;
        }
      } else {
        // Only add listeners if NOT signed
        paymentCards.forEach((card) => {
          card.addEventListener("click", handlePaymentClick);
        });
        supportCards.forEach((card) => {
          card.addEventListener("click", handleSupportClick);
        });
        if (supportNoneRadio) {
          supportNoneRadio.addEventListener(
            "change",
            handleSupportNoneRadioChange
          );
        }
      }
      // --- End: Conditional logic based on signed status ---

      // If a support card is clicked, uncheck the radio
      // (already handled in handleSupportClick)

      // --- Begin: Initial Highlighting based on fetched data ---
      if (
        document.selected_payment_option &&
        document.selected_payment_option !== "default"
      ) {
        const paymentCardToSelect = documentRef.current.querySelector(
          `.payment-option[data-payment-option="${document.selected_payment_option}"]`
        );
        if (paymentCardToSelect) {
          paymentCardToSelect.classList.add("selected");
          setSelectedPayment(document.selected_payment_option);
        }
      }

      if (document.selected_support_package) {
        if (document.selected_support_package === "none") {
          if (supportNoneRadio) {
            supportNoneRadio.checked = true;
          }
          setSelectedSupport("none"); // Initialize state
        } else {
          const supportCardToSelect = documentRef.current.querySelector(
            `.support-package[data-support-package="${document.selected_support_package}"]`
          );
          if (supportCardToSelect) {
            supportCardToSelect.classList.add("selected");
            setSelectedSupport(document.selected_support_package); // Initialize state
          }
        }
      }
      // --- End: Initial Highlighting ---

      // Cleanup listeners on unmount/content change
      return () => {
        // Only remove listeners if they were potentially added (i.e., not signed initially)
        if (!isSigned) {
          paymentCards.forEach((card) => {
            card.removeEventListener("click", handlePaymentClick);
          });
          supportCards.forEach((card) => {
            card.removeEventListener("click", handleSupportClick);
          });
          if (supportNoneRadio) {
            supportNoneRadio.removeEventListener(
              "change",
              handleSupportNoneRadioChange
            );
          }
        }
      };
      // --- End: Payment/Support Card Selection Logic ---
    }
    // Add document.signed_at as a dependency
  }, [document?.content, document?.signed_at]);

  // Sign button always enabled; selection requirements enforced on click
  // Mobile-only: append "(selected)" next to chosen packages
  useEffect(() => {
    if (!isMobile || !documentRef.current) return;
    // Remove any existing tags
    documentRef.current
      .querySelectorAll(".selected-tag")
      .forEach((el) => el.remove());
    // Payment option
    const payEl = documentRef.current.querySelector(".payment-option.selected");
    if (payEl) {
      const titleElem =
        payEl.querySelector("h3, h4, .title, label, span") || payEl;
      const tag = window.document.createElement("span");
      tag.textContent = " (selected)";
      tag.className = "selected-tag text-yellow-500 italic text-sm md:hidden";
      titleElem.appendChild(tag);
    }
    // Support package
    if (selectedSupport === "none") {
      const noneLab =
        documentRef.current.querySelector(
          "label[for='support-package-none-radio']"
        ) ||
        documentRef.current.querySelector("input#support-package-none-radio")
          ?.parentElement;
      if (noneLab) {
        const tag = window.document.createElement("span");
        tag.textContent = " (selected)";
        tag.className = "selected-tag text-blue-500 italic text-sm md:hidden";
        noneLab.appendChild(tag);
      }
    } else {
      const suppEl = documentRef.current.querySelector(
        ".support-package.selected"
      );
      if (suppEl) {
        const titleElem =
          suppEl.querySelector("h3, h4, .title, label, span") || suppEl;
        const tag = window.document.createElement("span");
        tag.textContent = " (selected)";
        tag.className = "selected-tag text-blue-500 italic text-sm md:hidden";
        titleElem.appendChild(tag);
      }
    }
  }, [isMobile, selectedSupport, selectedPayment]);

  // Your existing styles...
  const previewStyles = `
    /* Base layout */
    body {
      margin: 0;
      padding: 20px;
      background-color: #f0f0f0;
      min-height: 100vh;
    }

    .document-wrapper {
      max-width: 850px;
      margin: 40px auto;
      background: none;
    }

    .document-container {
      background-color: white;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
      border-radius: 8px;
      overflow: visible;
      margin: 0 auto;
      position: relative;
    }

    .document-content {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
    }

    /* Banner */
    .document-banner {
      width: 100%;
      height: 200px;
      overflow: hidden;
    }

    .document-banner img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* Typography */
    h1 {
      color: #4a5568;
      font-size: 24px;
      margin: 0 0 20px 0;
      text-align: center;
    }

    h2 {
      color: #4a5568;
      font-size: 20px;
      margin: 1.5em 0 0.8em 0;
    }

    p {
      margin: 0 0 1em 0;
    }

    p, ul, ol, li {
      color: #333;
    }

    strong {
      font-weight: bold;
    }

    .document-title {
      color: #4a5568;
      font-size: 24px;
      margin: 30px 0;
      text-align: center;
      padding: 0 40px;
    }

    .document-body {
      padding: 0 40px 40px 40px;
      color: #333;
    }

    /* Custom bullets */
    .document-content ul,
    .document-content ol {
      list-style: none;
      margin: 1em 0;
      padding: 0;
    }

    .document-content ul li,
    .document-content ol li {
      position: relative;
      margin: 0.75rem 0;
      padding-left: 1.5rem;
    }

    .document-content ul li::before,
    .document-content ol li::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.6em;
      width: 0.75rem;
      height: 0.75rem;
      background-color: #4299e1;
      border-radius: 50%;
      opacity: 0.8;
    }

    /* Footer / branding */
    .footer-divider {
      border: 0;
      background: rgba(0, 0, 0, 0.1);
      margin: 20px 0 10px;
    }

    .company-branding {
      text-align: center;
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e2e8f0;
      font-size: 14px;
      color: #718096;
    }

    .company-footer {
      text-align: center;
    }

    .company-name {
      font-weight: bold;
      font-size: 18px;
      margin-bottom: 4px;
    }

    .company-tagline {
      color: #718096;
      font-style: italic;
    }

    /* Signature */
    .signature-container {
      margin-top: 30px;
      border-top: 1px solid #e2e8f0;
      padding-top: 20px;
    }

    .signature-container.signed .signature-image-container {
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: 10px;
      background: #fff;
      margin: 15px 0;
    }

    .signature-image {
      width: 100%;
      max-height: 100px;
      object-fit: contain;
    }

    .signature-date {
      margin-top: 10px;
      color: #4a5568;
      font-size: 0.9em;
    }

    #sign-date {
      margin-top: 10px;
      font-style: italic;
      color: #4a5568;
    }

    .input-group {
      margin-bottom: 10px;
    }

    .poc-input {
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 16px;
      margin-bottom: 15px;
    }

    .poc-input:disabled {
      background-color: #f3f4f6;
      color: #4a5568;
      border-color: #e2e8f0;
      cursor: not-allowed;
    }

    #signature-pad,
    .signature-pad-canvas {
      border: 1px solid #000;
      width: 100%;
      margin-bottom: 10px;
      background-color: white;
    }

    .client-signature-section + .client-signature-section {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px dashed #e2e8f0;
    }

    .signature-status {
      color: #4a5568;
      font-size: 0.95em;
      margin: 12px 0;
    }

    .button-group {
      display: flex;
      justify-content: space-between;
      gap: 10px;
    }

    .action-button {
      padding: 10px 15px;
      background-color: #4a5568;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.3s;
    }

    .action-button:hover:not(:disabled) {
      background-color: #2d3748;
    }

    .action-button:disabled {
      background-color: #a0aec0;
      cursor: not-allowed;
      opacity: 0.7;
    }

    .action-button.downloading {
      position: relative;
      padding-left: 30px;
    }

    .action-button.downloading::before {
      content: '';
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 12px;
      height: 12px;
      border: 2px solid #fff;
      border-radius: 50%;
      border-top-color: transparent;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: translateY(-50%) rotate(360deg);
      }
    }

    /* Mobile responsive */
    @media (max-width: 640px) {
      body {
        padding: 4px;
      }

      .document-wrapper {
        margin: 8px auto;
      }

      .document-container {
        border-radius: 6px;
      }

      .document-title {
        font-size: 20px;
        padding: 0 16px;
        margin: 20px 0;
      }

      .document-body {
        padding: 0 16px 24px 16px;
      }

      .document-banner {
        height: 140px;
      }

      h1 {
        font-size: 20px;
      }

      h2 {
        font-size: 17px;
      }

      /* Signature area mobile */
      .signature-container {
        padding: 12px 0;
        margin-top: 16px;
      }

      #signature-pad,
      .signature-pad-canvas {
        height: 150px;
      }

      .button-group {
        flex-direction: column;
        gap: 8px;
      }

      .action-button {
        width: 100%;
        padding: 12px 15px;
        font-size: 15px;
        min-height: 44px;
      }

      .poc-input {
        font-size: 16px;
        padding: 10px;
      }

      .input-group {
        margin-bottom: 8px;
      }

      /* Payment form mobile */
      #payment-form-container,
      #payment-form {
        padding: 16px;
      }

      #payment-form-container > div,
      .payment-form {
        padding: 0;
      }

      /* Invoice tables mobile */
      table {
        font-size: 13px;
        width: 100%;
      }

      table th,
      table td {
        padding: 6px 8px;
        word-break: break-word;
      }

      /* Company footer mobile */
      .company-branding {
        margin-top: 20px;
        padding-top: 12px;
        font-size: 13px;
      }

      .company-name {
        font-size: 16px;
      }
    }

    /* Extra small screens (375px and below) */
    @media (max-width: 400px) {
      body {
        padding: 2px;
      }

      .document-wrapper {
        margin: 4px auto;
      }

      .document-title {
        font-size: 18px;
        padding: 0 12px;
      }

      .document-body {
        padding: 0 12px 20px 12px;
      }

      .document-banner {
        height: 120px;
      }

      h1 {
        font-size: 18px;
      }

      h2 {
        font-size: 16px;
      }

      table {
        font-size: 12px;
      }

      table th,
      table td {
        padding: 4px 6px;
      }
    }

    /* Print */
    @media print {
      body {
        background: none;
      }
      .document-wrapper {
        margin: 0;
        max-width: none;
      }
      .document-container {
        box-shadow: none;
      }
    }
  `;

  // Update the download button HTML structure when rendering the document content
  useEffect(() => {
    if (document?.content) {
      const content = document.content.replace(
        '<button id="download-button" class="action-button">Download as PDF</button>',
        `<button id="download-button" class="action-button ${
          isDownloading ? "downloading" : ""
        }" ${isDownloading ? "disabled" : ""}>
          ${isDownloading ? "Downloading..." : "Download as PDF"}
        </button>`
      );

      if (documentRef.current) {
        const downloadButton =
          documentRef.current.querySelector("#download-button");
        if (downloadButton) {
          downloadButton.className = `action-button ${
            isDownloading ? "downloading" : ""
          }`;
          downloadButton.disabled = isDownloading;
          downloadButton.textContent = isDownloading
            ? "Downloading..."
            : "Download as PDF";
        }
      }
    }
  }, [isDownloading, document?.content]);

  if (isLoading) return <div className="text-center mt-8">Loading...</div>;
  if (isError)
    return (
      <ErrorPage
        title="Document Error"
        message={error?.message || "Failed to load document"}
        errorCode={error?.status || "500"}
      />
    );
  if (!document)
    return (
      <ErrorPage
        title="Document Not Found"
        message="The document you're looking for might have been deleted or is no longer accessible."
        errorCode="404"
      />
    );

  // If showing success page, pass the document metadata
  if (showSuccessPage) {
    // Use stored documentId or get from sessionStorage as fallback
    const currentDocId = documentId || sessionStorage.getItem("lastDocumentId");

    return (
      <SignatureSuccess
        documentUrl={window.location.href}
        documentTitle={document?.title}
        expiryDate={document?.link_expires_at}
        documentId={currentDocId}
      />
    );
  }

  // PaymentForm.js is the single rendered payment UI for both the portal and
  // public share-link flows (billing-contract.md §4). Only show it for an
  // unpaid invoice document that actually has a Stripe secret to confirm.
  const showPaymentForm =
    document.document_type === "invoice" &&
    document.invoice_status !== "paid" &&
    document.invoice_status !== "refunded" &&
    document.invoice_status !== "canceled" &&
    !!document.client_secret;

  return (
    <>
      {/* Use CapabilitiesStyles for proposals/capabilities on all screen sizes */}
      {document.document_type === "capabilities" ||
      document.document_type === "proposal" ? (
        <CapabilitiesStyles />
      ) : (
        <style>{previewStyles}</style>
      )}

      {!hasSignatureBox(document.content) && (
        <FloatingDownloadButton
          isDownloading={isDownloading}
          onDownload={handleDownload}
        />
      )}

      <div
        className={`document-wrapper ${
          document.document_type === "capabilities" ||
          document.document_type === "proposal"
            ? "capabilities-wrapper"
            : ""
        }`}
      >
        <div
          className={`document-container ${
            document.document_type === "capabilities" ||
            document.document_type === "proposal"
              ? "capabilities-container"
              : ""
          }`}
        >
          <div
            ref={documentRef}
            className={`document-content ${
              document.document_type === "capabilities" ||
              document.document_type === "proposal"
                ? "capabilities-content"
                : ""
            }`}
          >
            {/* Use InvoiceContent component to handle copy functionality */}
            <InvoiceContent htmlContent={document.content} />
          </div>

          {/* Payment form for invoices */}
          {showPaymentForm && (
            <PaymentForm
              clientSecret={document.client_secret}
              setupIntentSecret={document.setup_intent_secret}
              invoiceId={document.invoice_id}
              invoiceType={document.invoice_type || "one_time"}
              email={document.client_email}
              name={document.client_name}
              amount={document.total}
              stripePk={document.stripe_pk}
              onSuccess={() => window.location.reload()}
            />
          )}
        </div>
      </div>

      <SignatureConfirmationPopup
        isOpen={showSignatureConfirm}
        onClose={() => setShowSignatureConfirm(false)}
        onConfirm={handleSignConfirm}
        name={pendingSignature?.name || ""}
        expectedName={pendingSignature?.expectedName || null}
        error={signError?.message}
        isLoading={isSigningDocument}
      />
    </>
  );
};

export default PublicDocumentView;
